/**
 * PageWithAnalytics Component
 * 
 * This component wraps page components and automatically sends screen/page view analytics events.
 * It also sets Atlassian user info for the analytics client to enable user-level tracking.
 * 
 * Analytics Strategy:
 * - Sends a screen event on mount/update with the provided screen name
 * - Includes optional attributes for enhanced context (entry points, feature gates, etc.)
 * - Properly memoizes object attributes to avoid unnecessary re-renders and event duplicates
 * - Passes user ID to the analytics service for user identification
 * 
 * @example
 * // Basic usage with screen name only
 * <PageWithAnalytics eventScreenName="homeScreen">
 *   <HomePage />
 * </PageWithAnalytics>
 * 
 * @example
 * // With memoized attributes from parent component
 * const screenAttributes = useMemo(() => ({
 *   entryPoint: 'championsHeroSection',
 *   pagePath: '/champions/signup',
 * }), []);
 * 
 * <PageWithAnalytics eventScreenName="championsSignupScreen" attributes={screenAttributes}>
 *   <ChampionsSignupPage />
 * </PageWithAnalytics>
 */
import React, { ReactNode, useContext, useEffect, useMemo } from 'react';
import { Attributes } from '@atlassiansox/analytics-web-client/dist/types/types';
import { UserContext } from 'context/UserContext';
import { sendScreenEvent } from 'services/analytics';

/**
 * Props for the PageWithAnalytics component
 * @property {string} eventScreenName - the name of the screen to send the page viewed event for
 * @property {Attributes} attributes - optional additional attributes to include in the screen event. 
 *                                     Should be memoized in parent component to avoid unnecessary events.
 * @property {ReactNode} children - child components to render
 */
interface PageWithAnalyticsProps {
  eventScreenName: string;
  attributes?: Attributes;
  children: ReactNode;
}

/**
 * PageWithAnalytics is a wrapper component that tracks page views and screen analytics.
 * 
 * Important: The attributes prop should be memoized in the parent component (using useMemo)
 * to prevent unnecessary re-renders. This component will use shallow comparison to detect
 * attribute changes, but object references must be stable.
 */
export const PageWithAnalytics: React.FC<PageWithAnalyticsProps> = ({
  eventScreenName,
  attributes,
  children,
}) => {
  const user = useContext(UserContext);
  const userId = user?.user?.account_id;

  // Memoize the final attributes object to ensure stable reference
  // This prevents unnecessary analytics events when attributes haven't actually changed
  const memoizedAttributes = useMemo(() => attributes || {}, [attributes]);

  useEffect(() => {
    sendScreenEvent(eventScreenName, memoizedAttributes, userId);
  }, [eventScreenName, userId, memoizedAttributes]);

  return <>{children}</>;
};
